// AGC: FinalizarLote - Screen to finalize lot (talhão) name entry
// AGC: [UNIFIED_FLOW] This screen now ONLY collects the lot name
// AGC: [UNIFIED_FLOW] After name entry, navigates to crop-suggestions for:
// AGC:   1. Climate-aware crop recommendations
// AGC:   2. Task planning
// AGC:   3. Input requirements (fertilizers, pesticides)
// AGC: This screen appears AFTER the lot boundaries are defined (via GPS, Map, or AI)
// AGC: Domain: Fazenda (Farm) → Talhões (Lots) hierarchy
import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  MapPin,
  Info,
  ChevronDown,
  Check,
  Pencil
} from 'lucide-react'
import { MapContainer, TileLayer, Polygon } from 'react-leaflet'
import { GlassCard } from '../shared'
import type { Screen, PlotFormData, Field } from '../../types'
import type { GeoPoint, LotMetrics } from '../../types/lot-detection'
import { useFarms } from '../../context'
import { useTranslation } from '../../hooks/useTranslation'
import { useKeyboardScroll } from '../../hooks/useKeyboardScroll'
import 'leaflet/dist/leaflet.css'

interface FinalizarLoteProps {
  onNavigate: (screen: Screen, data?: any) => void
  onBack: () => void
  coordinates?: GeoPoint[]
  metrics?: LotMetrics
  detectionMethod?: 'gps' | 'map' | 'ai' | 'ai-one-click' | 'auto-detect'
  // AGC: Pre-filled name from NovoLote phase 1 or voice command
  initialName?: string
  // AGC: Pre-selected farm ID from NovoLote phase 1
  initialFarmId?: string
  // AGC: Pre-filled area from voice command (optional)
  voicePrefillArea?: number
  // AGC: Pre-filled crop type from voice command (optional)
  voicePrefillCrop?: string
}

/**
 * AGC: FinalizarLote - Collect lot name before proceeding to crop recommendations
 * AGC: [UNIFIED_FLOW] This screen now ONLY collects the lot name
 * AGC: [UNIFIED_FLOW] After entering name, navigates to crop-suggestions where:
 * AGC:   - User sees climate-aware crop recommendations
 * AGC:   - User selects a crop
 * AGC:   - User proceeds to task/input planning
 * AGC:   - Lot is saved AFTER task planning (not here)
 * AGC: Called after lot boundaries are defined via AI One-Click or Auto-Detect
 */
export const FinalizarLote: React.FC<FinalizarLoteProps> = ({ 
  onNavigate, 
  onBack,
  coordinates = [],
  metrics,
  detectionMethod = 'ai',
  initialName,
  initialFarmId,
  voicePrefillArea,
  voicePrefillCrop
}) => {
  const { currentFarm, farms, isLoading: isFarmLoading, refreshFarms, setCurrentFarmById } = useFarms()
  const { t } = useTranslation()
  const { onFocus: handleInputFocus } = useKeyboardScroll()

  // AGC: Initialize lot name with pre-filled name from NovoLote or voice command
  const [lotName, setLotName] = useState(initialName || '')
  // AGC: When name is pre-filled, show as read-only; user taps pencil to edit
  const [isEditingName, setIsEditingName] = useState(!initialName)
  const [showNameError, setShowNameError] = useState(false)
  const [showFarmError, setShowFarmError] = useState(false)
  
  // AGC: Farm selector state
  // AGC: Priority: initialFarmId (from NovoLote) > currentFarm > first farm
  const [selectedFarm, setSelectedFarm] = useState<Field | null>(null)
  const [showFarmSelector, setShowFarmSelector] = useState(false)
  
  // AGC: Initialize selectedFarm when farms load
  // AGC: Uses initialFarmId from NovoLote if available (user already selected farm)
  useEffect(() => {
    if (!selectedFarm && farms.length > 0) {
      // AGC: Priority 1: Use pre-selected farm from NovoLote phase 1
      if (initialFarmId) {
        const preSelectedFarm = farms.find(f => f.id === initialFarmId)
        if (preSelectedFarm) {
          setSelectedFarm(preSelectedFarm)
          return
        }
      }
      // AGC: Priority 2: Single farm - auto-select
      if (farms.length === 1) {
        setSelectedFarm(farms[0])
      } else if (currentFarm) {
        // AGC: Priority 3: Last used farm
        setSelectedFarm(currentFarm)
      } else {
        setSelectedFarm(farms[0])
      }
    }
  }, [farms, currentFarm, selectedFarm, initialFarmId])

  // AGC: Calculate map center from coordinates, fallback to FARM coordinates
  const mapCenter = coordinates.length > 0
    ? {
        lat: coordinates.reduce((sum, p) => sum + p.lat, 0) / coordinates.length,
        lng: coordinates.reduce((sum, p) => sum + p.lng, 0) / coordinates.length,
      }
    : currentFarm?.coordinates 
      ? { lat: currentFarm.coordinates[0], lng: currentFarm.coordinates[1] }
      : { lat: -12.831672, lng: -55.794815 }

  // AGC: [UNIFIED_FLOW] Handle continue to crop recommendations
  // AGC: DO NOT save lot here - let field-task-planning handle it
  const handleContinue = async () => {
    // AGC: Validate lot name
    if (!lotName.trim()) {
      setShowNameError(true)
      return
    }
    
    // AGC: Use selectedFarm (from dropdown) or fallback to currentFarm
    const targetFarm = selectedFarm || currentFarm
    
    // AGC: CRITICAL - Validate that we have a farm to associate with
    if (!targetFarm?.id) {
      console.error('AGC: No farm selected!')
      setShowFarmError(true)
      
      // AGC: Try to refresh farms to get the default farm
      try {
        await refreshFarms()
      } catch (e) {
        console.error('AGC: Failed to refresh farms:', e)
      }
      return
    }
    
    // AGC: Update currentFarm to the selected farm for future operations
    if (selectedFarm && selectedFarm.id !== currentFarm?.id) {
      setCurrentFarmById(selectedFarm.id)
    }

    // AGC: [UNIFIED_FLOW] Convert coordinates for PlotFormData
    const plotCoordinates: [number, number][] = coordinates.map(p => [p.lat, p.lng])
    
    // AGC: [AUDIT_FIX] Generate lot ID early so ALL audit events use the same ID
    // AGC: This ensures recommendation audit, lot creation, and inputs all link to the same lot
    const generatedLotId = `plot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    // AGC: [UNIFIED_FLOW] Build PlotFormData to pass to crop-suggestions
    // AGC: ID is now generated here for consistent audit tracking
    // AGC: fieldId now uses the SELECTED farm from dropdown, not always currentFarm
    const plotFormData: PlotFormData = {
      id: generatedLotId, // AGC: [AUDIT_FIX] ID generated early for audit consistency
      name: lotName.trim(),
      area: metrics?.areaHectares || 0,
      cropType: '', // AGC: Will be selected in crop-suggestions
      coordinates: plotCoordinates,
      fieldId: targetFarm.id,
      // AGC: [UNIFIED_FLOW] Include metrics and detection method
      metrics: metrics,
      detectionMethod: detectionMethod,
      perimeter: metrics?.perimeterMeters,
      centroid: metrics?.centroid || { lat: mapCenter.lat, lng: mapCenter.lng },
    }
    
    console.log('🔄 AGC: [UNIFIED_FLOW] FinalizarLote → crop-suggestions')
    console.log('   Lot ID:', plotFormData.id) // AGC: [AUDIT_FIX] Log the generated ID
    console.log('   Name:', plotFormData.name)
    console.log('   Area:', plotFormData.area, 'ha')
    console.log('   Coordinates:', plotFormData.coordinates?.length, 'points')
    console.log('   Detection method:', detectionMethod)
    console.log('   Voice prefill crop:', voicePrefillCrop)
    
    // AGC: [UNIFIED_FLOW] Navigate to crop-suggestions with plot data
    // AGC: Include voicePrefillCrop so crop can be pre-selected from voice command
    onNavigate('crop-suggestions', { plotData: plotFormData, voicePrefillCrop })
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900">
      {/* AGC: Header */}
      <header className="bg-gradient-to-r from-[#0B5F4A] to-[#0A7B5C] px-4 pt-4 pb-4 sticky top-0 z-10">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white/90 hover:text-white mb-3"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('common.back')}
        </button>
        
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-yellow-300" />
          </div>
          <div>
            <h1 className="text-white text-xl font-bold">{t('fields.nameLot')}</h1>
            <p className="text-white/80 text-sm">
              {detectionMethod === 'ai' || detectionMethod === 'ai-one-click' 
                ? t('fields.aiDetected') 
                : detectionMethod === 'gps' 
                  ? t('fields.gpsCreated') 
                  : detectionMethod === 'auto-detect'
                    ? t('fields.autoDetected')
                    : t('fields.mapDrawn')}
            </p>
          </div>
        </div>
      </header>

      <div className="px-4 py-4 space-y-4 pb-8">
        {/* AGC: Progress indicator - Step 1 of 3 */}
        <div className="flex gap-2 mb-2">
          <div className="flex-1 h-1.5 rounded-full bg-emerald-500" />
          <div className="flex-1 h-1.5 rounded-full bg-gray-200" />
          <div className="flex-1 h-1.5 rounded-full bg-gray-200" />
        </div>
        <p className="text-xs text-gray-500 text-center mb-4">
          {t('fields.step1of3')}
        </p>

        {/* AGC: Map Preview */}
        {coordinates.length > 0 && (
          <GlassCard className="p-0 overflow-hidden">
            <div className="h-40 relative">
              <MapContainer
                center={[mapCenter.lat, mapCenter.lng]}
                zoom={14}
                className="h-full w-full"
                zoomControl={false}
                attributionControl={false}
              >
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                />
                <Polygon
                  positions={coordinates.map(p => [p.lat, p.lng])}
                  pathOptions={{
                    color: '#10B981',
                    fillColor: '#10B981',
                    fillOpacity: 0.3,
                    weight: 3,
                  }}
                />
              </MapContainer>
              
              {metrics && (
                <div className="absolute bottom-2 left-2 right-2 flex gap-2">
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-sm">
                    <span className="text-xs text-gray-500">{t('fields.area')}</span>
                    <p className="text-sm font-bold text-emerald-600">
                      {metrics.areaHectares.toFixed(1)} ha
                    </p>
                  </div>
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-sm">
                    <span className="text-xs text-gray-500">{t('fields.perimeter')}</span>
                    <p className="text-sm font-bold text-blue-600">
                      {(metrics.perimeterMeters / 1000).toFixed(2)} km
                    </p>
                  </div>
                </div>
              )}
            </div>
          </GlassCard>
        )}

        {/* AGC: Farm Status Indicator */}
        {isFarmLoading && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-blue-700 dark:text-blue-300">{t('fields.loadingFarms')}</p>
            </div>
          </div>
        )}
        
        {/* AGC: Farm Error Warning */}
        {showFarmError && !isFarmLoading && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-xl">⚠️</span>
              <div>
                <p className="text-sm font-medium text-red-700 dark:text-red-300">{t('fields.noFarmSelected')}</p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{t('fields.createFarmFirst')}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* AGC: Farm Selector - Shows dropdown for multiple farms, static for single farm */}
        {!isFarmLoading && farms.length > 0 && (
          <div className="relative">
            {farms.length === 1 ? (
              /* AGC: Single farm - show as read-only indicator */
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🏠</span>
                  <div>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">{t('fields.targetFarm')}</p>
                    <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">{farms[0].name}</p>
                  </div>
                </div>
              </div>
            ) : (
              /* AGC: Multiple farms - show dropdown selector */
              <>
                <button
                  onClick={() => setShowFarmSelector(!showFarmSelector)}
                  className="w-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3 flex items-center justify-between hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🏠</span>
                    <div className="text-left">
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">{t('fields.selectFarm')}</p>
                      <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                        {selectedFarm?.name || t('fields.chooseFarm')}
                      </p>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-emerald-600 dark:text-emerald-400 transition-transform ${showFarmSelector ? 'rotate-180' : ''}`} />
                </button>
                
                {/* AGC: Farm Dropdown */}
                {showFarmSelector && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg z-20 max-h-60 overflow-y-auto">
                    {farms.map((farm) => {
                      const isSelected = selectedFarm?.id === farm.id
                      const plotCount = farm.plots?.length || 0
                      
                      return (
                        <button
                          key={farm.id}
                          onClick={() => {
                            setSelectedFarm(farm)
                            setShowFarmSelector(false)
                            setShowFarmError(false)
                          }}
                          className={`w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                            isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {isSelected && <Check className="w-4 h-4 text-emerald-600" />}
                            <div>
                              <p className={`text-sm font-medium ${isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-gray-100'}`}>
                                {farm.name}
                              </p>
                              {farm.location && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">{farm.location}</p>
                              )}
                            </div>
                          </div>
                          {plotCount > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded-md bg-gray-100 dark:bg-slate-600 text-gray-600 dark:text-gray-300">
                              {plotCount} {plotCount === 1 ? 'lot' : 'lots'}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* AGC: Talhão Name - read-only when pre-filled, editable on pencil tap */}
        <GlassCard>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <MapPin className="w-4 h-4 inline mr-1.5 text-emerald-600" />
            {t('fields.lotName')}
          </label>
          {isEditingName ? (
            /* AGC: Editable input mode */
            <input
              type="text"
              value={lotName}
              onChange={(e) => {
                setLotName(e.target.value)
                if (showNameError) setShowNameError(false)
              }}
              onFocus={handleInputFocus}
              onKeyDown={(e) => {
                // AGC: Android keyboard "Go" / desktop Enter → trigger Continuar button
                if (e.key === 'Enter' && lotName.trim() && !isFarmLoading) {
                  e.preventDefault()
                  handleContinue()
                }
              }}
              enterKeyHint="go"
              placeholder={t('fields.lotNamePlaceholder')}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-lg dark:bg-slate-800 dark:text-gray-100 ${
                showNameError
                  ? 'border-red-400 focus:ring-red-400 bg-red-50 dark:bg-red-900/20'
                  : 'border-gray-200 dark:border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'
              }`}
              autoFocus
            />
          ) : (
            /* AGC: Read-only display with pencil to edit */
            <div className="flex items-center justify-between px-4 py-3 border-2 border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
              <span className="text-lg font-medium text-gray-900 dark:text-gray-100">{lotName}</span>
              <button
                type="button"
                onClick={() => setIsEditingName(true)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-800/30 transition-colors"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>
          )}
          {showNameError && (
            <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
              {t('fields.lotNameRequired')}
            </p>
          )}
        </GlassCard>

        {/* AGC: Inline Continuar button - right below name input, visible with keyboard */}
        <button
          onClick={handleContinue}
          disabled={!lotName.trim() || isFarmLoading}
          className="w-full py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <span>{t('fields.continueToCrop')}</span>
          <ArrowRight className="w-5 h-5" />
        </button>

        {/* AGC: [UNIFIED_FLOW] Info about next steps - below button */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-1">
                {t('fields.nextSteps')}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-300">
                {t('fields.nextStepsDescription')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FinalizarLote

